import { createServerFn } from '@tanstack/react-start';
import { getRequestHeader } from '@tanstack/react-start/server';
import { WORLDCOO_MERCHANT_ID } from '@worldcoo/monolith/core';
import {
	sketchDonationInputSchema,
	paymentMethodsInputSchema,
	paymentDetailsInputSchema,
	processPaymentInputSchema,
} from '@worldcoo/monolith/payments/shared';
import {
	type SketchPortalDonationArgs,
	sketchDonation,
	getAdyenPaymentMethods,
	getAdyenPaymentDetails,
	initPayment,
} from '@worldcoo/monolith/payments';
import { applicationType } from '@worldcoo/monolith/shared-context';
import { postContactForm } from '@worldcoo/monolith/apis/portals';
import { contactFormSchema } from '@worldcoo/monolith/portals/contact-form-schema';
import { eventEmitter } from './server-broadcast.server';
import { cache } from './server-cache.server';
import { portalAuthorCacheOptions } from './server-cache-options.server';
import * as z from 'zod';
import { parseRequest, parseIp } from './server-functions-shared.server';

export const preregisterDonationFn = createServerFn({ method: 'POST' })
	.inputValidator(sketchDonationInputSchema)
	.handler(async ({ data }) => {
		const requestIp = parseIp();
		if (!requestIp) throw new Error('Cannot get request IP');
		const request = parseRequest();

		const applicationId = data.applicationId;
		if (applicationId.type !== applicationType.portal) {
			throw new Error('Only portal application type is supported');
		}
		const portalAuthor = await cache(portalAuthorCacheOptions(applicationId.host));
		if ('notFound' in portalAuthor) throw new Error('Portal not found');

		const merchantId = portalAuthor.type === 'merchant' ? portalAuthor.merchantId : WORLDCOO_MERCHANT_ID;

		return sketchDonation({
			sourceIp: requestIp,
			origin: request.baseUrl,
			language: request.locale,
			userAgent: getRequestHeader('User-Agent') ?? undefined,
			merchantId,
			input: data,
		} as SketchPortalDonationArgs);
	});

export const getPaymentsMethodsFn = createServerFn({ method: 'POST' })
	.inputValidator(paymentMethodsInputSchema)
	.handler(({ data }) => {
		const requestIp = parseIp();
		if (!requestIp) throw new Error('Cannot get request IP');

		return getAdyenPaymentMethods({ sourceIp: requestIp, input: data });
	});

export const getPaymentDetailsFn = createServerFn({ method: 'POST' })
	.inputValidator(paymentDetailsInputSchema)
	.handler(({ data }) => {
		const requestIp = parseIp();
		if (!requestIp) throw new Error('Cannot get request IP');

		return getAdyenPaymentDetails({ sourceIp: requestIp, input: data });
	});

export const processPaymentFn = createServerFn({ method: 'POST' })
	.inputValidator(processPaymentInputSchema)
	.handler(({ data }) => {
		const request = parseRequest();
		const requestIp = parseIp();
		if (!requestIp) throw new Error('Cannot get request IP');

		return initPayment({
			sourceIp: requestIp,
			origin: request.baseUrl,
			input: data,
		});
	});

export const waitForConfirmedDonationFn = createServerFn()
	.inputValidator(z.string())
	.handler(async function* ({ data: donationIdToConfirm }) {
		const promise = new Promise<'donation.confirmed' | 'donation.timeout'>((resolve) => {
			const onConfirmed = (donationId: string) => {
				if (donationId === donationIdToConfirm) {
					eventEmitter.off('donation.confirmed', onConfirmed);
					resolve('donation.confirmed');
				}
			};

			eventEmitter.on('donation.confirmed', onConfirmed);

			setTimeout(() => {
				eventEmitter.off('donation.confirmed', onConfirmed);
				resolve('donation.timeout');
			}, 10_000);
		});

		yield await promise;
	});

export const postContactFormFn = createServerFn({ method: 'POST' })
	.inputValidator(contactFormSchema)
	.handler(async ({ data }) => {
		const request = parseRequest();
		const requestIp = parseIp();
		if (!requestIp) throw new Error('Cannot get request IP');

		return postContactForm(data, {
			ip: requestIp,
			language: data.language,
			origin: request.baseUrl,
			userAgent: getRequestHeader('User-Agent') ?? '',
		});
	});
